import { sortBy } from '@vrplatform/utils';
import type { HasuraClient } from '@vrplatform/graphql';
import { getCountryByIsoCode, getStateByIsoCode } from '../iso-3166';
import { whereOwners } from '../queries';
import { formatUserName, getListingName, getSourceDescription } from '../utils';

export const ownersCsvQuery = async (
  client: HasuraClient,
  input: Parameters<typeof whereOwners>[0],
  data: {
    accountingAppId: string | undefined;
  }
) => {
  const accountingAppId = data.accountingAppId;
  const rightType = `${accountingAppId}.vendor`;
  const leftType = 'finalytic.owner';
  const settingKey = 'vendor';

  const rows = await client.query((q) => {
    return q
      .owners({
        where: whereOwners(input),
        order_by: [{ name: 'asc_nulls_first' }],
      })
      .map((owner) => {
        const listings =
          owner
            .ownerships({
              order_by: [{ listing: { name: 'asc_nulls_last' } }],
            })
            .map((x) => getListingName(x.listing)) || 'missing';

        const userAccess = owner
          .userAccesses()
          .map((x) => formatUserName(x.user) || 'missing');

        const vendor = !accountingAppId
          ? ''
          : owner
              .settingsRight({
                where: {
                  rightType: { _eq: rightType },
                  leftType: { _eq: leftType },
                  key: { _eq: settingKey },
                  parentSettingId: { _is_null: true },
                  localAutomationId: { _is_null: true },
                },
                order_by: [{ updated_at: 'desc' }],
                limit: 1,
              })
              .map((x) => getSourceDescription(x.rightSource))[0] || '';

        return {
          Name: owner.name?.trim() || '',
          'First Name': owner.firstName?.trim() || '',
          Type:
            owner.type === 'individual'
              ? 'Individual'
              : owner.companyType || 'Company',
          Email: owner.email,
          Phone: owner.phone,
          'Tax ID': owner.taxId,
          Vendor: vendor,
          Listings: sortBy(listings).join(', '),
          'User Access': sortBy(userAccess).join(', '),
          // Address
          'Line 1': owner.address?.line || '',
          'Line 2': owner.address?.lineDetails || '',
          City: owner.address?.city || '',
          'Postal Code': owner.address?.postalCode || '',
          'State Code': owner.address?.stateCode || '',
          State:
            getStateByIsoCode(
              owner.address?.stateCode || '',
              owner.address?.countryCode || ''
            )?.name || '',
          'Country Code': owner.address?.countryCode || '',
          Country:
            getCountryByIsoCode(owner.address?.countryCode || '')?.name || '',
        };
      });
  });

  return {
    rows: sortBy(rows, 'Name'),
  };
};
